/* prettier-ignore */

import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, HostListener, input, InputSignal, Output} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import * as L from 'leaflet';
import 'leaflet-gpx';

@Component({
    selector: "app-details-gpx-map",
    imports: [
        CommonModule,
        TranslateModule,
    ],  
    standalone: true,
    templateUrl: "./map.component.html",
    styleUrls: ["./map.component.scss"],
})

export class MapComponent {
    @Output() public elevationProfileEmitter: EventEmitter<any> = new EventEmitter<any>();
    public gpxData: InputSignal<any> = input<any>();
    public coordinates: InputSignal<any> = input<any>();
    private map!: L.Map;
    private gpxLayer: any;
    private lastBounds: L.LatLngBound;

    gpxDataEffect = effect(() => {
        const gpxData = this.gpxData();
        if (!gpxData)
            return;

        this.drawGpx(gpxData);
    })

    ngAfterViewInit(): void {
        this.initMap();
        this.fixLayerView();
    }

    private initMap(): void {
        this.map = L.map('gpx-route-map', {
            center: this.coordinates(),
            zoom: 13,
            scrollWheelZoom: false
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: null,
        }).addTo(this.map);
    }

    private drawGpx(gpxData: string): void {
        if (this.gpxLayer) 
            this.map.removeLayer(this.gpxLayer);

        this.gpxLayer = new L.GPX(gpxData, {
            async: true,
            markers: {
                startIcon: '/assets/imgs/pin-icon-start.png',
                endIcon: '/assets/imgs/pin-icon-end.png',
                wptIcons: { '' : '/assets/imgs/pin-icon-end.png' },
                
            },
            polyline_options: [
                { color: 'blue', opacity: 0.75, weight: 6 },
                { color: 'red', opacity: 0.75, weight: 6 },
                { color: 'orange', opacity: 0.75, weight: 6 },
            ],
        })
        .on('addpoint', (e: any) => {
            const marker: L.Marker = e.point;
            const pointType: string = e.point_type; // 'start' | 'end' | 'waypoint' | 'label'
            const element: Element = e.element;     // nodo XML original

            const name = element.querySelector('name')?.textContent ?? '';
            const desc = element.querySelector('desc')?.textContent ?? '';
            marker.bindPopup(
                `<strong>${name || pointType}</strong><br>${desc || ''}`
            );
        })
        .on('loaded', (e: any) => {
            const gpx = e.target;
            this.lastBounds = gpx.getBounds();

            if (document.visibilityState === 'visible') 
                this.map.fitBounds(this.lastBounds);

            this.fixLayerView();

            const elevRaw = gpx.get_elevation_data();
            this.emitElevation(elevRaw);

            gpx.on('click', (evt: any) => {
                const layer = evt.layer;
                const latlng = evt.latlng;
                if (!layer || typeof layer.getLatLngs !== 'function') 
                    return;

                this.onTrackClick(latlng, gpx, layer);
            });
        })
        .addTo(this.map);
    }

    private emitElevation(elevation){
        const elevationProfile = elevation?.map(([dist, elev]) => ({
            distance: dist,
            elevation: elev,
        }));

        if (!elevationProfile || !elevationProfile.length)
            return;

        this.elevationProfileEmitter.emit(elevationProfile);
    }

    private onTrackClick(evt: L.LeafletMouseEvent, gpx: any, line: L.Polyline): void {
        const clickLatLng = evt;
        const latlngs = line.getLatLngs() as L.LatLng[];
        let closestIndex = 0;
        let minDist = Infinity;

        latlngs.forEach((pt, idx) => {
            const d = clickLatLng.distanceTo(pt);
            if (d < minDist) {
                minDist = d;
                closestIndex = idx;
            }
        });

        const elevData = gpx.get_elevation_data() as [number, number, string][];
        const [distKm, elevM] = elevData[closestIndex] || [0, 0];
        const html = `
            <div>
                <strong>Km ${distKm.toFixed(2)}</strong>
                <br>
                Altura: ${Math.round(elevM)} m<br>
            </div>
        `;

        L.popup().setLatLng(clickLatLng).setContent(html).openOn(this.map);
    }

    @HostListener('window:focus')
    onWindowFocus() {
        this.fixLayerView();
    }

    @HostListener('document:visibilitychange')
    onVisibilityChange() {
        if (document.visibilityState === 'visible') 
            this.fixLayerView();
    }

    private fixLayerView(){
        setTimeout(() => {
            this.invalidateMapSize();
        }, 250);
    }

    private invalidateMapSize() {
        if (!this.map) 
            return;

        this.map.invalidateSize();
        if (this.lastBounds) {
            this.map.fitBounds(this.lastBounds, { animate: false });
            return;
        }

        const center = this.map.getCenter();
        const zoom = this.map.getZoom();
        this.map.setView(center, zoom, { animate: false });
    }

}